import * as Sentry from '@sentry/nextjs';
import { hasNonEmptyDuplicate } from '@winging/util/helper/string.util';
import { useTranslations } from 'next-intl';
import React, {
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAtomValue } from 'jotai';
import { chatroomIdSelector } from '@/entities/chat';
import DemoGuideMessage from '@/features/demo/ui/DemoGuideMessage';
import { useWindowWidth } from '@/shared/lib/hooks/useWindowSize';
import { isValidItemUrl } from '@/shared/lib/url';
import { useRouter } from '@/i18n/navigation';
import { constructAgentDB } from '@/features/demo/api/builderCreator.chatMessage.service';
import { uploadFile } from '@/shared/api/file.service';
import { useAuth } from '@/shared/providers/AuthProvider';
import {
  AgentConstructionItem,
  AgentConstructionRequest,
} from '@/entities/agent/model/agent.types';
import DemoAgentItemsUploader from '@/features/demo/ui/DemoAgentItemsUploader';
import DemoPrivacyAgreement from '@/features/demo/ui/DemoPrivacyAgreement';
import DemoAuthenticatedActions from '@/features/demo/ui/DemoAuthenticatedActions';
import DemoUnauthenticatedActions from '@/features/demo/ui/DemoUnauthenticatedActions';
import DemoAuthForm from '@/features/demo/ui/DemoAuthForm';
import DemoResponse from '@/features/demo/ui/DemoResponse';
import { useToast } from '@/shared/providers/ToastProvider';
import { getDataScopes } from '@/entities/dataScope/api/dataScope.service';
import {
  getActiveDataScopes,
  getDefaultDataScopeRefs,
} from '@/entities/dataScope/lib/dataScopeRefs';
import { DataScope } from '@/entities/dataScope/model/dataScope.types';

const INITIAL_ITEM: AgentConstructionItem = {
  title: '',
  url: '',
  documents: [],
};
const SCROLL_DELAY_MS = 300;
const GUIDE_REDIRECT_DELAY_MS = 3000;
const RESPONSE_WORD_INTERVAL_MS = 100;

export default function DemoRequestItem({
  shouldAnimate,
}: {
  shouldAnimate: boolean;
}) {
  const t = useTranslations('Demo');
  const { showToast } = useToast();
  const width = useWindowWidth();
  const isDesktopWidth = typeof width === 'number' && width >= 1280;
  const router = useRouter();

  const { user } = useAuth();
  const chatroomId = useAtomValue(chatroomIdSelector);

  const [showAuthForm, setShowAuthForm] = useState(false);
  const [isSendRequest, setIsSendRequest] = useState(false);
  const [agentId, setAgentId] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [privacyChecked, setPrivacyChecked] = useState(false);
  const [onClickSubmit, setOnClickSubmit] = useState(false);
  const [agentDataUploaderErrors, setAgentDataUploaderErrors] = useState<
    Record<number, string | null>
  >({});
  const [dataScopes, setDataScopes] = useState<DataScope[]>([]);

  const [isGuideMessageDone, setIsGuideMessageDone] = useState<boolean>(false);

  const scrollTargetRef = useRef<HTMLDivElement>(null);
  const dataUploaderScrollTargetRef = useRef<HTMLDivElement>(null);

  const [demoResponseVisibleWordCount, setDemoResponseVisibleWordCount] =
    useState(0);
  const [constructionRequestDto, setConstructionRequestDto] =
    useState<AgentConstructionRequest>({
      user_id: '',
      company_id: '',
      chat_room_id: '',
      company_ref: '',
      items: [INITIAL_ITEM],
    });

  const demoResponseText = t('데모 요청이 완료되었습니다');
  const demoResponseTextArray = useMemo(
    () => demoResponseText.split(' '),
    [demoResponseText]
  );

  const scrollBlock: ScrollLogicalPosition = isDesktopWidth
    ? 'nearest'
    : 'start';

  useDelayedScrollIntoView(scrollTargetRef, shouldAnimate, scrollBlock);
  useDelayedScrollIntoView(
    dataUploaderScrollTargetRef,
    shouldAnimate && isGuideMessageDone,
    scrollBlock
  );

  useEffect(() => {
    if (!chatroomId) {
      return;
    }

    setConstructionRequestDto((prev) => ({
      ...prev,
      chat_room_id: chatroomId,
    }));
  }, [chatroomId]);

  useEffect(() => {
    if (!user?.company_ref?.data_scope_enabled) {
      setDataScopes([]);
      return;
    }

    let ignore = false;

    getDataScopes()
      .then((scopes) => {
        if (ignore) {
          return;
        }

        const activeScopes = getActiveDataScopes(scopes);
        const defaultDataScopeRefs = getDefaultDataScopeRefs(
          activeScopes,
          user
        );

        setDataScopes(activeScopes);
        setConstructionRequestDto((prev) => ({
          ...prev,
          items: prev.items.map((item) => ({
            ...item,
            data_scope_refs: item.data_scope_refs?.length
              ? item.data_scope_refs
              : defaultDataScopeRefs,
          })),
        }));
      })
      .catch((error) => {
        Sentry.captureException(error);
      });

    return () => {
      ignore = true;
    };
  }, [user]);

  const defaultDataScopeRefs = useMemo(
    () => getDefaultDataScopeRefs(dataScopes, user),
    [dataScopes, user]
  );

  useEffect(() => {
    if (!isSendRequest) {
      return;
    }

    let redirectTimeout: ReturnType<typeof setTimeout> | undefined;
    const interval = window.setInterval(() => {
      setDemoResponseVisibleWordCount((prev) => {
        if (prev >= demoResponseTextArray.length) {
          clearInterval(interval);

          redirectTimeout = setTimeout(() => {
            router.push(`/agent/training/${agentId}`);
          }, GUIDE_REDIRECT_DELAY_MS);
          return prev;
        }
        return prev + 1;
      });
    }, RESPONSE_WORD_INTERVAL_MS);

    return () => {
      clearInterval(interval);
      if (redirectTimeout) {
        clearTimeout(redirectTimeout);
      }
    };
  }, [agentId, demoResponseTextArray.length, isSendRequest, router]);

  const handleUpdateItem = useCallback(
    (
      index: number,
      updater: (prevItem: AgentConstructionItem) => AgentConstructionItem
    ) => {
      setConstructionRequestDto((prev) => {
        const updatedItems = [...prev.items];
        updatedItems[index] = updater(updatedItems[index]);
        return {
          ...prev,
          items: updatedItems,
        };
      });
      setErrorMsg('');
    },
    []
  );

  const handleRemoveItem = useCallback((index: number) => {
    setConstructionRequestDto((prev) => {
      const updatedItems = prev.items.filter((_, i) => i !== index);
      return {
        ...prev,
        items: updatedItems,
      };
    });
  }, []);

  const handleAddItem = useCallback(() => {
    setConstructionRequestDto((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          ...INITIAL_ITEM,
          documents: [],
          data_scope_refs: defaultDataScopeRefs,
        },
      ],
    }));
    setErrorMsg('');
  }, [defaultDataScopeRefs]);

  const resetSubmitState = useCallback(() => {
    setOnClickSubmit(false);
  }, []);

  const setUploaderError = useCallback(
    (index: number, error: string | null) => {
      setAgentDataUploaderErrors((prev) => ({
        ...prev,
        [index]: error,
      }));
    },
    []
  );

  const togglePrivacyChecked = useCallback(() => {
    setPrivacyChecked((prev) => !prev);
  }, []);

  const handleSubmit = useCallback(async () => {
    const dto: AgentConstructionRequest = {
      ...constructionRequestDto,
      company_ref: user?.company_ref?._id || '',
      company_id: user?.company_ref?.company_id || '',
      user_id: user?._id || '',
      items: constructionRequestDto.items.map((item) => ({
        ...item,
        data_scope_refs: item.data_scope_refs?.length
          ? item.data_scope_refs
          : defaultDataScopeRefs,
      })),
    };
    const titles = dto.items.map((item) => item.title);
    const urls = dto.items.map((item) => item.url);
    const uniqueTitles = Array.from(new Set(titles));
    const filesToUpload: {
      itemIndex: number;
      documentIndex: number;
      file: File;
    }[] = [];

    dto.items.forEach((item, itemIndex) => {
      item.documents.forEach((document, documentIndex) => {
        filesToUpload.push({
          itemIndex,
          documentIndex,
          file: document.file!,
        });
      });
    });

    const fileKeys = filesToUpload.map(
      ({ file }) => `${file.name}-${file.size}-${file.lastModified}`
    );

    if (hasNonEmptyDuplicate(urls)) {
      setErrorMsg(t('중복된 URL이 입력되었습니다'));
      resetSubmitState();
      return;
    }

    if (!urls.every((url) => !url || isValidItemUrl(url))) {
      setErrorMsg(t('유효하지 않은 URL이 입력되었습니다'));
      resetSubmitState();
      return;
    }

    if (uniqueTitles.length !== titles.length) {
      setErrorMsg(t('아이템 이름이 중복되었습니다'));
      resetSubmitState();
      return;
    }

    if (
      uniqueTitles.length !== uniqueTitles.filter((title) => !!title).length
    ) {
      setErrorMsg(t('아이템 이름을 입력해주세요'));
      resetSubmitState();
      return;
    }

    if (hasNonEmptyDuplicate(fileKeys)) {
      setErrorMsg(t('중복된 파일이 첨부되었습니다'));
      resetSubmitState();
      return;
    }

    if (!filesToUpload.length && urls.every((url) => !url)) {
      setErrorMsg(t('학습할 아이템이 등록되지 않았습니다'));
      resetSubmitState();
      return;
    }
    if (
      Object.values(agentDataUploaderErrors).some((error) => error !== null)
    ) {
      resetSubmitState();
      return;
    }

    if (onClickSubmit) {
      // 중복 제출 안내도 locale 메시지에서 관리한다.
      return showToast(t('에이전트가 생성 중입니다 잠시만 기다려주세요'));
    }

    setOnClickSubmit(true);

    try {
      const uploadTasks = filesToUpload.map((fileToUpload) =>
        uploadFile(fileToUpload.file, true, user?.company_ref?._id || '').then(
          (uploadedUrl) => {
            const document =
              dto.items[fileToUpload.itemIndex].documents[
                fileToUpload.documentIndex
              ];
            dto.items[fileToUpload.itemIndex].documents[
              fileToUpload.documentIndex
            ] = {
              url: uploadedUrl,
              name: document.name,
              size: document.size,
            };
          }
        )
      );

      await Promise.all(uploadTasks);

      const constructRes = await constructAgentDB(dto);
      setAgentId(constructRes);
      setIsSendRequest(true);
    } catch (e) {
      Sentry.captureException(e);
      resetSubmitState();
    }
  }, [
    agentDataUploaderErrors,
    constructionRequestDto,
    defaultDataScopeRefs,
    onClickSubmit,
    resetSubmitState,
    t,
    user,
  ]);

  if (!shouldAnimate) {
    return null;
  }

  return (
    <>
      <DemoGuideMessage
        ref={scrollTargetRef}
        shouldAnimate={shouldAnimate}
        setIsGuideMessageDone={setIsGuideMessageDone}
      />
      {isGuideMessageDone && (
        <>
          <DemoAgentItemsUploader
            ref={dataUploaderScrollTargetRef}
            items={constructionRequestDto.items}
            disabled={onClickSubmit}
            onAddItem={handleAddItem}
            onRemoveItem={handleRemoveItem}
            onUpdateItem={handleUpdateItem}
            onErrorChange={setUploaderError}
          />
          <DemoPrivacyAgreement
            checked={privacyChecked}
            toggleChecked={togglePrivacyChecked}
            label={t('개인정보가 포함되어 있는지 확인해주세요')}
            viewMoreLabel={t('(전문 보기)')}
          />
          <DemoAuthenticatedActions
            isVisible={Boolean(user) && !isSendRequest}
            buttonWidth={calculateButtonWidth(
              width,
              constructionRequestDto.items.length
            )}
            disabled={!privacyChecked || onClickSubmit}
            errorMsg={errorMsg}
            onSubmit={handleSubmit}
            loading={onClickSubmit}
            buttonLabel={t('AI 에이전트 생성하기')}
          />
          <DemoUnauthenticatedActions
            isVisible={!showAuthForm && !user}
            onShowAuthForm={() => setShowAuthForm(true)}
            buttonLabel={t('로그인 후 데모 확인하기')}
          />
          <DemoAuthForm isVisible={showAuthForm && !user} />
          <DemoResponse
            isVisible={isSendRequest}
            words={demoResponseTextArray.slice(0, demoResponseVisibleWordCount)}
          />
        </>
      )}
    </>
  );
}

function useDelayedScrollIntoView(
  ref: RefObject<HTMLDivElement | null>,
  shouldScroll: boolean,
  block: ScrollLogicalPosition
) {
  useEffect(() => {
    if (!shouldScroll) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      ref.current?.scrollIntoView({
        behavior: 'smooth',
        block,
      });
    }, SCROLL_DELAY_MS);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [block, shouldScroll]);
}

function calculateButtonWidth(
  width: number | null | undefined,
  itemLength: number
): string {
  if (!width || width < 1280) {
    return '100%';
  }

  return itemLength === 1 ? '584px' : '888px';
}
